import axios from 'axios';
import api from '../../../../services/api';

// =============================================================================
// API CONFIGURATION
// =============================================================================

const NEWSLETTER_API_URL = ' https://apinewsletter.mnmks.com';

// =============================================================================
// SUMMARY API - Single Function
// =============================================================================

/**
 * Get or generate summary for an article
 * 
 * Flow:
 * 1. Checks database for cached summary
 * 2. If not found, generates via AI
 * 3. Saves to database
 * 4. Returns result
 * 
 * @param {string} url - The article URL
 * @param {string} newsId - The news article ID (required for caching)
 * @param {string} userCompany - The user's company name (optional, for personalized insights)
 * @returns {Promise<Object>} - { description, so_what, cached }
 */
export const getSummary = async (url, newsId, userCompany = '') => {
    if (!url) {
        throw new Error('Article URL is required');
    }
    if (!newsId) {
        throw new Error('News ID is required');
    }

    try {
        const response = await api.post('/news/summary', {
            url: url,
            news_id: newsId,
            user_company: userCompany
        });

        if (!response.data.success) {
            throw new Error(response.data.message || 'Failed to get summary');
        }

        return {
            description: response.data.description || '',
            so_what: response.data.so_what || '',
            cached: response.data.cached || false
        };
    } catch (error) {
        console.error('Error getting summary:', error);

        if (error.response) {
            throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
        } else if (error.request) {
            throw new Error('Unable to connect to server. Make sure the Flask backend is running.');
        }
        throw error;
    }
};

// =============================================================================
// NEWS SHARING API
// =============================================================================

/**
 * Share news article(s) to recipients via email
 * Each news item can include:
 * - news_id, title, url, summary (if user generated)
 * - news_types, published_date, companies, locations, sri_rank_value
 */
export const shareNews = async ({ senderName, companyName, receiverEmails, newsItems, comments }) => {
    try {
        // Ensure all metadata fields are included for each news item
        const formattedNewsItems = newsItems.map(item => ({
            news_id: item.news_id,
            title: item.title || '',
            url: item.url || '',
            summary: item.summary || '',
            news_types: item.news_types || item.newsTypes || [],
            published_date: item.published_date || item.publishedDate || '',
            companies: item.companies || [],
            locations: item.locations || [],
            sri_rank_value: item.sri_rank_value || item.sriRankValue || 0
        }));

        const payload = {
            sender_name: senderName,
            company_name: companyName,
            receiver_emails: receiverEmails,
            news_items: formattedNewsItems
        };

        // Only include comments if provided
        if (comments && comments.trim()) {
            payload.comments = comments;
        }

        const response = await api.post('/news/share', payload);
        return response.data;
    } catch (error) {
        console.error('Error sharing news:', error);
        throw error;
    }
};

/**
 * Share a single news article (legacy format)
 */
export const shareNewsArticle = async ({ senderName, senderEmail, receiverEmail, newsId, title, url, summary }) => {
    return shareNews({
        senderName,
        senderEmail,
        receiverEmails: [receiverEmail],
        newsItems: [{ news_id: newsId, title, url, summary }]
    });
};

// =============================================================================
// FEEDBACK API
// =============================================================================

/**
 * Submit feedback for a news article
 * Includes article_title and article_url for storage in database
 */
export const submitFeedback = async ({ username, companyName, comment, importance, newsItems }) => {
    try {
        // Ensure article title and URL are included for each news item
        const formattedNewsItems = newsItems.map(item => ({
            news_id: item.news_id,
            title: item.title || '',
            url: item.url || ''
        }));

        const response = await api.post('/news/feedback', {
            username,
            company_name: companyName,
            comment,
            importance,
            news_items: formattedNewsItems
        });
        return response.data;
    } catch (error) {
        console.error('Error submitting feedback:', error);
        throw error;
    }
};

// =============================================================================
// COMPANY INFO API
// =============================================================================

export const getCompanyInfo = async ({ companyCode }) => {
    try {
        const response = await api.post('/news/company_info', {
            company_name: companyCode
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching company info:', error);
        throw error;
    }
};

/**
 * Report missing data for a company to trigger analyst investigation
 */
export const reportMissingData = async ({ userFirstName, userLastName, userCompany, searchedCompany }) => {
    try {
        const response = await api.post('/news/company_info_missing', {
            user_first_name: userFirstName,
            user_last_name: userLastName,
            user_company: userCompany,
            searched_company: searchedCompany
        });
        return response.data;
    } catch (error) {
        console.error('Error reporting missing data:', error);
        throw error;
    }
};

// =============================================================================
// HEALTH CHECK
// =============================================================================

/**
 * Check Flask backend health status
 */
export const checkHealth = async () => {
    try {
        const response = await api.get('/news/health');
        return response.data;
    } catch (error) {
        console.error('Backend health check failed:', error);
        return { status: 'error', message: error.message };
    }
};

// =============================================================================
// LIKES APIs
// =============================================================================

/**
 * Add, update, or remove a like/dislike on an article
 */
export const addLike = async ({ isLiked, entityCode, config = {} }) => {
    const payload = {
        is_liked: isLiked,
        entity_code: entityCode,
        ...(config.account_code && { account_code: config.account_code }),
        ...(config.account_name && { account_name: config.account_name }),
        ...(config.client_name && { client_name: config.client_name }),
        ...(config.client_code && { client_code: config.client_code }),
        ...(config.section && { section: config.section })
    };

    try {
        const response = await api.post('/news/likes/add_like', payload);
        return response.data;
    } catch (error) {
        console.error('Error adding like:', error);
        throw error;
    }
};

/**
 * Get the list of likes for the current user
 */
export const getLikeList = async ({ entityCode = '', config = {} } = {}) => {
    const params = {};
    if (config.account_name) params.account_name = config.account_name;
    if (config.client_name) params.client_name = config.client_name;
    if (config.section) params.section = config.section;
    if (config.account_code) params.account_code = config.account_code;
    if (config.client_code) params.client_code = config.client_code;
    if (entityCode) params.entity_code = entityCode;

    try {
        const response = await api.get('/news/likes/get_like_list', { params });
        return response.data?.likes || [];
    } catch (error) {
        console.error('Error fetching like list:', error);
        throw error;
    }
};

/**
 * Convert like list to map for quick lookup
 */
export const likesToMap = (likes) => {
    const map = new Map();
    likes.forEach(like => {
        // API returns article_id, not entity_code
        const id = like.article_id || like.entity_code;
        if (id) {
            map.set(id, like.is_liked);
        }
    });
    return map;
};

// =============================================================================
// STORY LINE
// =============================================================================
export const getStoryline = async () => {
    try {
        const response = await api.get('/news/get_storyline');
        return response.data || {};
    } catch (error) {
        console.error('Error fetching storyline:', error);
        throw error;
    }
};

// =============================================================================
// CONSUMER INDUSTRY
// =============================================================================
export const consumerIndustry = async ({ companyName }) => {
    try {
        const response = await api.post('/news/consumer_industry', {
            company_name: companyName
        });
        return response.data || {};
    } catch (error) {
        console.error('Error fetching consumer industry:', error);
        throw error;
    }
};

// =============================================================================
// NEWSLETTER API
// =============================================================================

/**
 * Fetches newsletter data from the backend API
 */
export const fetchNewsletterData = async (options = {}) => {
    const {
        filterdata = {
            labels: [],
            newstypes: [],
            regions: [],
            companies: [],
            dates: ["2025-12-01", "2026-01-15"]
        },
        sortvalue = 'sri_rank_value',
        skip = 0,
        limit = 100,
        searchterm = ""
    } = options;

    try {
        const formData = new FormData();
        formData.append('filterdata', JSON.stringify(filterdata));
        formData.append('sortvalue', sortvalue);
        formData.append('skip', skip.toString());
        formData.append('limit', limit.toString());
        formData.append('searchtearm', searchterm);

        const response = await axios.post(`${NEWSLETTER_API_URL}/news/get_data`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return response.data?.records || [];
    } catch (error) {
        console.error('Error fetching newsletter data:', error);
        throw error;
    }
};

/**
 * Format date to readable format
 * Supports both epoch timestamp (ms) and ISO date string
 */
export const formatPublishedDate = (dateValue) => {
    if (!dateValue) return '';

    let date;
    if (typeof dateValue === 'number') {
        // Epoch timestamp in milliseconds
        date = new Date(dateValue);
    } else if (typeof dateValue === 'string') {
        // ISO date string like "2025-12-23T19:43:00"
        date = new Date(dateValue);
    } else {
        return '';
    }

    // Check if date is valid
    if (isNaN(date.getTime())) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * Transform API record to component props
 */
export const transformRecordToProps = (record) => ({
    id: record._id?.$oid || record._id || record.news_id?.$oid || Math.random().toString(36).substr(2, 9),
    title: record.title || '',
    url: record.url || '',
    summary: record.summary || '',
    newsTypes: record.news_types || [],
    alertLabel: record.alert_label || '',
    publishedDate: formatPublishedDate(record.publishedAt || record.published_date?.$date),
    companies: record.companies || [],
    locations: record.locations || [],
    sriRankValue: record.sri_rank_value || 0,
    newsType: record.news_type || '',
});

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

const backendApi = {
    // Summary - single function
    getSummary,

    // News Sharing
    shareNews,
    shareNewsArticle,

    // Feedback
    submitFeedback,

    // Health Check
    checkHealth,

    // Consumer Industry
    consumerIndustry,

    // Reporting
    reportMissingData,

    // Likes
    addLike,
    getLikeList,
    likesToMap,

    // Newsletter
    fetchNewsletterData,
    formatPublishedDate,
    transformRecordToProps
};

export default backendApi;